import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RefreshCw, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { useAuthLite } from '@/context/AuthLite';
import { useToast } from '@/hooks/use-toast';

interface WebhookLog {
  id: string;
  source: 'facebook' | 'google' | 'zapier';
  timestamp: string;
  status: 'success' | 'error' | 'partial';
  payloadSize: number;
  mappingProfileId?: string;
  mappingErrors: Array<{ targetPath: string; reason: string }>;
  leadId?: string;
  contactId?: string;
  extractedFields?: Record<string, any>;
  rawPayload?: unknown;
  message?: string;
  processingSteps?: {
    leadgen_id?: string;
    deal_diagnostics?: Record<string, unknown>;
    events?: Array<{
      leadgen_id?: string;
      quote_quality?: string;
      idempotent_skip?: boolean;
      missing_fields?: string[];
      error?: string;
      deal_diagnostics?: Record<string, unknown>;
      contact_match?: string;
      contact_action?: string;
    }>;
    webhook_outcome?: string;
    idempotent_count?: number;
  };
  leadgenIds?: string[];
}

interface FacebookLeadListEntry {
  leadgen_id?: string;
  field_data?: Array<{ name?: string; values?: string[] }>;
}

function extractDealDiagnostics(log: WebhookLog): Record<string, unknown> | undefined {
  const steps = log.processingSteps as Record<string, unknown> | undefined;
  const root = steps?.deal_diagnostics;
  if (root && typeof root === 'object' && !Array.isArray(root)) {
    return root as Record<string, unknown>;
  }
  const ev = steps?.events as Array<{ deal_diagnostics?: unknown }> | undefined;
  const fromEvent = ev?.find((e) => e.deal_diagnostics != null)?.deal_diagnostics;
  if (fromEvent && typeof fromEvent === 'object' && !Array.isArray(fromEvent)) {
    return fromEvent as Record<string, unknown>;
  }
  return undefined;
}

function extractLeadgenId(log: WebhookLog): string | null {
  const steps = log.processingSteps as Record<string, unknown> | undefined;
  if (steps && typeof steps.leadgen_id === 'string') return steps.leadgen_id;
  const events = steps?.events as Array<{ leadgen_id?: string }> | undefined;
  if (events?.[0]?.leadgen_id) return String(events[0].leadgen_id);
  if (log.leadgenIds?.[0]) return log.leadgenIds[0];
  const raw = log.rawPayload as {
    entry?: Array<{ changes?: Array<{ value?: { leadgen_id?: string } }> }>;
  } | undefined;
  const v = raw?.entry?.[0]?.changes?.[0]?.value?.leadgen_id;
  return v != null ? String(v) : null;
}

interface WebhookHistoryProps {
  source?: 'facebook' | 'google' | 'zapier';
  limit?: number;
}

export default function WebhookHistory({ source, limit = 50 }: WebhookHistoryProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthLite();
  const { toast } = useToast();
  const isAdmin = user?.role === 'Admin';
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'success' | 'error' | 'partial'>('all');
  const [retryingLeadgen, setRetryingLeadgen] = useState<string | null>(null);
  const [forceNewQuote, setForceNewQuote] = useState(false);
  const [dealNamesById, setDealNamesById] = useState<Record<string, string>>({});
  const [contactNamesById, setContactNamesById] = useState<Record<string, string>>({});
  const [leadPreviewByLeadgenId, setLeadPreviewByLeadgenId] = useState<Record<string, string>>({});
  const dateLocale = i18n.language === 'sk' ? 'sk-SK' : i18n.language === 'hu' ? 'hu-HU' : 'en-GB';

  useEffect(() => {
    setForceNewQuote(false);
  }, [expandedId]);

  const runFacebookRetry = async (leadgenId: string) => {
    setRetryingLeadgen(leadgenId);
    try {
      const res = await fetch('/api/admin/facebook-lead-retry', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadgen_id: leadgenId, force_new_quote: forceNewQuote }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        toast({ title: t('mappingsExtra.facebookRetryAdminOnly'), variant: 'destructive' });
        return;
      }
      if (res.status === 404) {
        toast({ title: t('mappingsExtra.facebookRetryNoDeal'), variant: 'destructive' });
        return;
      }
      if (!res.ok) {
        toast({
          title: t('mappingsExtra.facebookRetryFailed'),
          description: typeof data.error === 'string' ? data.error : data.message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: data.initial_create
          ? t('mappingsExtra.facebookRetryDealCreated')
          : t('mappingsExtra.facebookRetryDone'),
        description:
          data.quote_skip_reason === 'already_priced_use_force_new_quote'
            ? t('mappingsExtra.facebookForceNewQuoteHint')
            : data.quote_action === 'created' || data.quote_action === 'skipped'
              ? `${data.new_quote_quality ?? ''}`
              : data.quote_skip_reason ?? undefined,
      });
      await loadLogs();
    } catch (e) {
      toast({ title: t('mappingsExtra.facebookRetryFailed'), variant: 'destructive' });
    } finally {
      setRetryingLeadgen(null);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [source, filter]);

  useEffect(() => {
    const loadRelatedNames = async () => {
      const dealIds = Array.from(
        new Set(logs.map((l) => l.leadId).filter((id): id is string => typeof id === 'string' && id.length > 0))
      ).filter((id) => !dealNamesById[id]);
      const contactIds = Array.from(
        new Set(logs.map((l) => l.contactId).filter((id): id is string => typeof id === 'string' && id.length > 0))
      ).filter((id) => !contactNamesById[id]);

      if (dealIds.length === 0 && contactIds.length === 0) return;

      if (dealIds.length > 0) {
        try {
          const params = new URLSearchParams({ ids: dealIds.join(',') });
          const res = await fetch(`/api/crm/deals/by-ids?${params.toString()}`, {
            credentials: 'include',
          });
          if (res.ok) {
            const data = await res.json().catch(() => ({} as Record<string, unknown>));
            const deals = Array.isArray((data as { deals?: unknown[] }).deals)
              ? ((data as { deals?: Array<Record<string, unknown>> }).deals ?? [])
              : [];
            const nextDeals: Record<string, string> = {};
            for (const d of deals) {
              const id = typeof d.id === 'string' ? d.id : '';
              const title =
                typeof d.title === 'string'
                  ? d.title
                  : typeof d.name === 'string'
                    ? d.name
                    : typeof d.deal_name === 'string'
                      ? d.deal_name
                      : '';
              if (id && title) nextDeals[id] = title;
            }
            if (Object.keys(nextDeals).length > 0) {
              setDealNamesById((prev) => ({ ...prev, ...nextDeals }));
            }
          }
        } catch (e) {
          console.error('Failed to resolve deal names:', e);
        }
      }

      if (contactIds.length > 0) {
        try {
          const results = await Promise.all(
            contactIds.map(async (id) => {
              try {
                const res = await fetch(`/api/crm/contacts/${id}`, {
                  credentials: 'include',
                });
                if (!res.ok) return { id, name: '' };
                const data = await res.json().catch(() => ({} as Record<string, unknown>));
                const name =
                  typeof (data as { name?: unknown }).name === 'string'
                    ? (data as { name: string }).name
                    : typeof (data as { full_name?: unknown }).full_name === 'string'
                      ? (data as { full_name: string }).full_name
                      : '';
                return { id, name };
              } catch {
                return { id, name: '' };
              }
            })
          );
          const nextContacts: Record<string, string> = {};
          for (const r of results) {
            if (r.name) nextContacts[r.id] = r.name;
          }
          if (Object.keys(nextContacts).length > 0) {
            setContactNamesById((prev) => ({ ...prev, ...nextContacts }));
          }
        } catch (e) {
          console.error('Failed to resolve contact names:', e);
        }
      }
    };

    void loadRelatedNames();
  }, [logs, dealNamesById, contactNamesById]);

  useEffect(() => {
    const leadgenIds = Array.from(
      new Set(
        logs
          .filter((l) => l.source === 'facebook')
          .map((l) => extractLeadgenId(l))
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    ).filter((id) => !leadPreviewByLeadgenId[id]);

    if (!isAdmin || leadgenIds.length === 0) return;

    const loadLeadPreviews = async () => {
      try {
        const params = new URLSearchParams({
          unified: '1',
          per_form_limit: '50',
        });
        const res = await fetch(`/api/admin/facebook-lead-list?${params.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({} as Record<string, unknown>));
        const leads = Array.isArray((data as { leads?: unknown[] }).leads)
          ? ((data as { leads?: FacebookLeadListEntry[] }).leads ?? [])
          : [];

        const nextMap: Record<string, string> = {};
        for (const lead of leads) {
          const leadgenId = typeof lead.leadgen_id === 'string' ? lead.leadgen_id : '';
          if (!leadgenId) continue;
          const fd = Array.isArray(lead.field_data) ? lead.field_data : [];
          const email = fd.find((f) => String(f?.name ?? '').toLowerCase() === 'email')?.values?.[0];
          const name = fd.find((f) =>
            ['full_name', 'name', 'first_name'].includes(String(f?.name ?? '').toLowerCase())
          )?.values?.[0];
          const preview = [name, email].filter(Boolean).join(' - ') || email || name || '';
          if (preview) nextMap[leadgenId] = preview;
        }

        if (Object.keys(nextMap).length > 0) {
          setLeadPreviewByLeadgenId((prev) => ({ ...prev, ...nextMap }));
        }
      } catch (e) {
        console.error('Failed to resolve Facebook lead previews:', e);
      }
    };

    void loadLeadPreviews();
  }, [logs, isAdmin, leadPreviewByLeadgenId]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.append('source', source);
      if (filter !== 'all') params.append('status', filter);
      params.append('limit', limit.toString());

      const res = await fetch(`/api/webhook-logs?${params.toString()}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      } else if (res.status === 404) {
        // Endpoint not implemented yet – show empty state
        setLogs([]);
      }
    } catch (e) {
      console.error('Failed to load webhook logs:', e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            ✓ {t('mappingsExtra.successful')}
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
            ⚠ {t('mappingsExtra.partial')}
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
            ✗ {t('mappingsExtra.error')}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getSourceBadge = (source: string) => {
    const colors: Record<string, string> = {
      facebook: 'bg-blue-100 text-blue-800',
      google: 'bg-purple-100 text-purple-800',
      zapier: 'bg-pink-100 text-pink-800',
    };
    return (
      <Badge className={`${colors[source] || ''}`}>
        {source}
      </Badge>
    );
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString(dateLocale);
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-card border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('mappingsExtra.webhookHistory')}</h3>
        <Button
          onClick={loadLogs}
          disabled={loading}
          size="sm"
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('mappingsExtra.refresh')}
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded text-sm ${
            filter === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {t('mappingsExtra.all')}
        </button>
        <button
          onClick={() => setFilter('success')}
          className={`px-3 py-1 rounded text-sm ${
            filter === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {t('mappingsExtra.successful')}
        </button>
        <button
          onClick={() => setFilter('partial')}
          className={`px-3 py-1 rounded text-sm ${
            filter === 'partial'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {t('mappingsExtra.partial')}
        </button>
        <button
          onClick={() => setFilter('error')}
          className={`px-3 py-1 rounded text-sm ${
            filter === 'error'
              ? 'bg-red-100 text-red-800'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {t('mappingsExtra.error')}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-foreground/60">
          {t('mappingsExtra.loading')}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-foreground/60">
          <p>{t('mappingsExtra.noWebhookData')}</p>
          <p className="text-sm mt-1">{t('mappingsExtra.noWebhookDataHint')}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {logs.map((log) => {
            const dealDiagnostics =
              log.source === 'facebook' ? extractDealDiagnostics(log) : undefined;
            const leadgenId = log.source === 'facebook' ? extractLeadgenId(log) : null;
            const leadPreview = leadgenId ? leadPreviewByLeadgenId[leadgenId] : undefined;
            return (
            <div
              key={log.id}
              className="border rounded p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <button
                onClick={() =>
                  setExpandedId(expandedId === log.id ? null : log.id)
                }
                className="w-full text-left flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1">
                  {expandedId === log.id ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getSourceBadge(log.source)}
                      {getStatusBadge(log.status)}
                    </div>
                    <div className="text-xs text-foreground/60 space-y-0.5">
                      <div>{formatDate(log.timestamp)}</div>
                      {(leadPreview || leadgenId) && (
                        <div>
                          Lead:{' '}
                          <span className="text-[11px]">
                            {leadPreview || leadgenId}
                          </span>
                        </div>
                      )}
                      {log.leadId && (
                        <div>
                          Deal:{' '}
                          <span className="text-[11px]">
                            {dealNamesById[log.leadId] || log.leadId}
                          </span>
                        </div>
                      )}
                      {log.contactId && (
                        <div>
                          Contact:{' '}
                          <span className="text-[11px]">
                            {contactNamesById[log.contactId] || log.contactId}
                          </span>
                        </div>
                      )}
                      {log.processingSteps?.events?.[0]?.quote_quality && (
                        <div>
                          Quote:{' '}
                          {log.processingSteps.events[0].quote_quality === 'priced'
                            ? 'Árkalkulált'
                            : log.processingSteps.events[0].quote_quality === 'placeholder'
                              ? 'Előkészített (ár nélkül)'
                              : log.processingSteps.events[0].quote_quality === 'skipped_size'
                                ? t('mappingsExtra.webhookQuoteSkippedSize')
                                : 'Sikertelen'}
                        </div>
                      )}
                      {log.processingSteps?.events?.[0]?.idempotent_skip && (
                        <div className="text-amber-700">Idempotens – már feldolgozva</div>
                      )}
                      {log.processingSteps?.webhook_outcome === 'incomplete_contact_data' && (
                        <div className="text-amber-800">Hiányos kontakt adat</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-foreground/60 ml-2">
                  {formatBytes(log.payloadSize)}
                </div>
              </button>

              {expandedId === log.id && (
                <div className="mt-3 pt-3 border-t text-xs space-y-2">
                  {log.mappingErrors && log.mappingErrors.length > 0 && (
                    <div>
                      <p className="font-semibold text-red-600 mb-1">
                        {t('mappingsExtra.mappingErrorsCount', { count: log.mappingErrors.length })}
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-red-600">
                        {log.mappingErrors.map((err, i) => (
                          <li key={i}>
                            <strong>{err.targetPath}</strong>: {err.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {dealDiagnostics && (
                    <div>
                      <p className="font-semibold mb-1">{t('mappingsExtra.dealDiagnostics')}</p>
                      <pre className="bg-background p-2 rounded border overflow-x-auto text-xs max-h-56 whitespace-pre-wrap">
                        {JSON.stringify(dealDiagnostics, null, 2)}
                      </pre>
                    </div>
                  )}

                  {log.extractedFields && (
                    <div>
                      <p className="font-semibold mb-1">{t('mappingsExtra.extractedData')}</p>
                      <pre className="bg-background p-2 rounded border overflow-x-auto text-xs">
                        {JSON.stringify(log.extractedFields, null, 2)}
                      </pre>
                    </div>
                  )}

                  <div>
                    <p className="font-semibold mb-1">{t('mappingsExtra.fullPayload')}</p>
                    <pre className="bg-background p-2 rounded border overflow-x-auto text-xs max-h-48">
                      {JSON.stringify(log.rawPayload, null, 2)}
                    </pre>
                  </div>

                  {log.source === 'facebook' && isAdmin && (
                    <div className="rounded border border-border bg-muted/20 p-3 space-y-2">
                      <p className="font-semibold">{t('mappingsExtra.facebookRetry')}</p>
                      {(() => {
                        const lid = extractLeadgenId(log);
                        if (!lid) {
                          return (
                            <p className="text-foreground/60">{t('mappingsExtra.facebookRetryNoLeadgen')}</p>
                          );
                        }
                        return (
                          <>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`force-sf-${log.id}`}
                                checked={forceNewQuote}
                                onCheckedChange={(c) => setForceNewQuote(c === true)}
                              />
                              <Label htmlFor={`force-sf-${log.id}`} className="text-xs font-normal cursor-pointer">
                                {t('mappingsExtra.facebookForceNewQuote')}
                              </Label>
                            </div>
                            <p className="text-[11px] text-foreground/60">{t('mappingsExtra.facebookForceNewQuoteHint')}</p>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={retryingLeadgen === lid}
                              className="gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                void runFacebookRetry(lid);
                              }}
                            >
                              <RotateCcw className={`w-3.5 h-3.5 ${retryingLeadgen === lid ? 'animate-spin' : ''}`} />
                              {retryingLeadgen === lid
                                ? t('mappingsExtra.facebookRetryRunning')
                                : t('mappingsExtra.facebookRetry')}
                            </Button>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
